import { MenuItem, Stack, Typography } from '@mui/material';
import SelectV2 from '../../../Common/Form/SelectV2';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import UserStorePageStore from '../../../UserData/_stores/Store/UserStorePageStore';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import { FetchUserStorePages } from '../../../UserData/_actions/Store/UserStorePageActions';

type Props = {
	value: string
	onChange: (value: string) => void
	error?: boolean
}

export default function SquarespaceStorePageField(props: Props) {
	const [t] = useTranslation('account-v2')
	const dispatch = useAppDispatch()
	const storePages = useAppSelector(state => state.get('userData').get('storePages'))
	const [loadError, setLoadError] = useState(false)

	useEffect(() => {
		let active = true
		setLoadError(false)

		dispatch(FetchUserStorePages('squarespace').set({
			onSuccess: () => {
				if (active) {
					setLoadError(false)
				}
			},
			onFailure: () => {
				if (!active) {
					return
				}

				setLoadError(true)
				props.onChange('')
			},
		}))

		return () => {
			active = false
		}
	}, [dispatch, props.onChange])

	const renderItem = useCallback((page: UserStorePageStore) => {
		return <MenuItem key={page.get('id')} value={page.get('id')}>{page.get('title')}</MenuItem>
	}, [])

	const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		props.onChange(String(e.target.value))
	}, [props.onChange])

	const caption = useMemo(() => {
		if (loadError) {
			return t('Unable to load shop pages. Please try again later.');
		}

		if (props.error) {
			return t('Please select a store page.');
		}

		return storePages.count() === 0
			? t('Loading shop pages...')
			: t('Required for Squarespace publishing.');
	}, [loadError, props.error, storePages, t])

	return <Stack spacing={0.75} sx={{ maxWidth: 420 }}>
		<SelectV2
			label={t('Store page')}
			value={props.value}
			onChange={onChange}
			disabled={storePages.count() === 0 || loadError}
			error={loadError || props.error}
		>
			<MenuItem value="" disabled>{t('Select an option...')}</MenuItem>
			{storePages.valueSeq().map(renderItem)}
		</SelectV2>
		<Typography variant="caption" color={loadError || props.error ? 'error' : 'text.secondary'}>
			{caption}
		</Typography>
	</Stack>
}
